/*
 * Обращения к серверу.
 *
 * Токен доступа живёт только в этой переменной — не в localStorage.
 * Любой посторонний сценарий, попавший на страницу, вычитал бы его оттуда
 * мгновенно. Токен обновления браузер хранит сам в печенье httpOnly,
 * и сценарий до него не дотягивается вовсе.
 */

import type { MaintenanceState } from '@andresot/protocol/envelope';
import { network } from '$stores/network.svelte';

let accessToken: string | null = null;
let authorizationGeneration = 0;

/** Операция принадлежит уже завершённой сессии. */
export class SessionChangedError extends Error {
	constructor() {
		super('Сессия изменилась');
		this.name = 'SessionChangedError';
	}
}

export function getAuthorizationGeneration(): number {
	return authorizationGeneration;
}

export function assertAuthorizationGeneration(generation: number): void {
	if (generation !== authorizationGeneration) throw new SessionChangedError();
}

/** Новый вход отменяет прежнюю попытку, даже пока токен ещё не получен. */
export function beginAuthorization(): number {
	refreshing = null;
	return ++authorizationGeneration;
}

/**
 * Чем кончилось обновление токена.
 *
 * Раньше здесь было «да/нет», и сетевой сбой не отличался от завершённой
 * сессии: приложение стирало токен и считало человека вышедшим, хотя он всего
 * лишь зашёл в лифт.
 */
export type RefreshResult = 'ok' | 'expired' | 'offline';

/** Обновление, идущее прямо сейчас. Нужно, чтобы не запускать его дважды. */
let refreshing: { generation: number; promise: Promise<RefreshResult> } | null = null;

export class ApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
		readonly details?: Record<string, unknown>
	) {
		super(message);
		this.name = 'ApiError';
	}
}

/**
 * До сервера не дошли вовсе.
 *
 * Отличается от `ApiError` по существу: там сервер ответил и объяснил отказ,
 * здесь ответа не было. Экранам это нужно, чтобы не показывать «неверный
 * пароль» там, где просто нет сети.
 */
export class NetworkError extends Error {
	constructor(message = 'Нет связи с сервером') {
		super(message);
		this.name = 'NetworkError';
	}
}

/**
 * Единственное место, где видно, дошёл ли запрос.
 *
 * `fetch` бросает исключение только когда до сервера не добрались; 401 и 500
 * приходят обычным ответом. По этому и различаем «сети нет» и «сервер отказал».
 */
/**
 * Сколько ждать ответа, прежде чем считать запрос потерянным.
 *
 * У соединения такой предел есть давно — десять секунд. У обычных запросов не
 * было вовсе: на слабой связи запрос висел, пока не сдастся сам браузер, а это
 * на iPhone бывает и минута. Всё это время экран ждал молча.
 */
const REQUEST_TIMEOUT_MS = 20_000;

async function fetchTracked(
	path: string,
	init: RequestInit,
	/*
	 * Предел ставится не всем подряд. У скачивания файла — почты, записи
	 * звонка, выгрузки — медленная связь означает долгий ответ, а не потерю:
	 * оборвать его на двадцатой секунде значило бы сделать большие файлы
	 * недоступными как раз там, где они и так даются тяжело.
	 */
	deadline = false
): Promise<Response> {
	const startedAt = Date.now();
	try {
		const response = await fetch(path, {
			...init,
			signal: init.signal ?? (deadline ? AbortSignal.timeout(REQUEST_TIMEOUT_MS) : null)
		});
		network.reportSuccess(Date.now() - startedAt);
		return response;
	} catch {
		network.reportFailure();
		throw new NetworkError();
	}
}

/**
 * Чтение повторяется один раз.
 *
 * На слабой связи запрос теряется чаще, чем кажется, а перечитать список
 * безопасно: повтор ничего не меняет. Отправку так повторять нельзя — для неё
 * есть очередь, которая ждёт возвращения связи и шлёт с тем же
 * идентификатором.
 *
 * Повтор один: если и он не дошёл, дело не в случайной потере, и мучить
 * человека ожиданием ещё двадцать секунд незачем.
 */
async function fetchWithRetry(
	path: string,
	init: RequestInit,
	retry: boolean,
	generation: number
): Promise<Response> {
	try {
		return await fetchTracked(path, init, true);
	} catch (error) {
		assertAuthorizationGeneration(generation);
		if (!retry) throw error;
		return fetchTracked(path, init, true);
	}
}

/*
 * Кто узнаёт о технических работах.
 *
 * Слой запросов не тянет за собой хранилище: иначе получился бы круг —
 * хранилищу нужны запросы, запросам нужно хранилище. Подписку ставит тот,
 * кому это состояние нужно показывать.
 */
let maintenanceListener: ((state: MaintenanceState) => void) | null = null;

export function onMaintenance(listener: (state: MaintenanceState) => void): void {
	maintenanceListener = listener;
}

export function setAccessToken(token: string | null, expectedGeneration?: number): void {
	if (expectedGeneration !== undefined) assertAuthorizationGeneration(expectedGeneration);
	beginAuthorization();
	accessToken = token;
}

/** Новый короткий токен той же сессии не означает смену аккаунта. */
export function renewAccessToken(token: string, generation: number): void {
	assertAuthorizationGeneration(generation);
	accessToken = token;
}

export function hasAccessToken(): boolean {
	return accessToken !== null;
}

/**
 * Токен для установки соединения. Браузерный WebSocket не позволяет задать
 * заголовки при подключении, поэтому токен передаётся в адресе — он короткий
 * и живёт 15 минут.
 */
export function getAccessToken(): string | null {
	return accessToken;
}

/** Двоичные запросы (части файла) с тем же обновлением токена, что у JSON API. */
export async function authorizedFetch(path: string, init: RequestInit): Promise<Response> {
	const generation = authorizationGeneration;
	const sendRaw = () => {
		assertAuthorizationGeneration(generation);
		const headers = new Headers(init.headers);
		if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
		return fetchTracked(path, { ...init, headers, credentials: 'same-origin' });
	};
	let response = await sendRaw();
	assertAuthorizationGeneration(generation);
	if (response.status === 401 && (await refreshOnce(generation)) === 'ok')
		response = await sendRaw();
	assertAuthorizationGeneration(generation);
	return response;
}

interface UploadOptions {
	method: string;
	headers: Record<string, string>;
	body: Blob;
	signal: AbortSignal;
	/** Сколько байт этой части уже ушло на сервер. */
	onProgress?: (sent: number) => void;
}

/**
 * То же самое, но на XMLHttpRequest — ради одной вещи: `fetch` не сообщает,
 * сколько байт уже отправлено. Без этого доля показывалась только по концу
 * куска, а кусок у нас 8 МБ: почти всякая отправка с телефона до самого
 * конца выглядела застывшей на нуле.
 *
 * Ответ возвращается настоящим `Response`, чтобы вызывающий код разбирал
 * его так же, как ответ `authorizedFetch`.
 */
export async function authorizedUpload(path: string, options: UploadOptions): Promise<Response> {
	const generation = authorizationGeneration;
	const sendRaw = () =>
		new Promise<Response>((resolve, reject) => {
			assertAuthorizationGeneration(generation);
			if (options.signal.aborted) {
				reject(new DOMException('Загрузка отменена', 'AbortError'));
				return;
			}

			const xhr = new XMLHttpRequest();
			xhr.open(options.method, path, true);
			xhr.withCredentials = true;
			for (const [name, value] of Object.entries(options.headers)) {
				xhr.setRequestHeader(name, value);
			}
			if (accessToken) xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);

			const onAbort = () => xhr.abort();
			options.signal.addEventListener('abort', onAbort, { once: true });
			const done = () => options.signal.removeEventListener('abort', onAbort);

			xhr.upload.onprogress = (event) => options.onProgress?.(event.loaded);
			xhr.onerror = () => {
				done();
				reject(new ApiError('Не удалось передать часть файла', 0));
			};
			xhr.onabort = () => {
				done();
				reject(new DOMException('Загрузка отменена', 'AbortError'));
			};
			xhr.onload = () => {
				done();
				const headers = new Headers();
				for (const line of xhr
					.getAllResponseHeaders()
					.trim()
					.split(/[\r\n]+/u)) {
					const index = line.indexOf(':');
					if (index > 0) headers.set(line.slice(0, index), line.slice(index + 1).trim());
				}
				// У ответов без тела своё правило: Response с телом их не принимает.
				const empty = xhr.status === 204 || xhr.status === 205 || xhr.status === 304;
				resolve(new Response(empty ? null : xhr.responseText, { status: xhr.status, headers }));
			};

			xhr.send(options.body);
		});

	let response = await sendRaw();
	assertAuthorizationGeneration(generation);
	if (response.status === 401 && (await refreshOnce(generation)) === 'ok')
		response = await sendRaw();
	assertAuthorizationGeneration(generation);
	return response;
}

interface RequestOptions {
	/** PUT — для замены целиком там, где повторный вызов ничего не меняет. */
	method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	body?: unknown;
	headers?: Record<string, string>;
	/** Не пытаться обновить токен при отказе — для самих запросов входа. */
	skipRefresh?: boolean;
	/** Контекст операции, начатой до нескольких ожиданий (например, капчи). */
	generation?: number;
	/** Ответ выхода нужен для снятия отметки, даже после локальной очистки. */
	allowSessionChange?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
	const generation = options.generation ?? authorizationGeneration;
	assertAuthorizationGeneration(generation);
	try {
		let response = await send(path, options, generation);

		/*
		 * Отказ по истёкшему токену — обычное дело: он живёт 15 минут. Пробуем
		 * обновить и повторяем запрос один раз. Повторять бесконечно нельзя:
		 * при по-настоящему завершённой сессии это дало бы вечный цикл.
		 */
		if (!options.allowSessionChange) assertAuthorizationGeneration(generation);
		if (response.status === 401 && !options.skipRefresh && generation === authorizationGeneration) {
			const refreshed = await refreshOnce(generation);
			assertAuthorizationGeneration(generation);

			if (refreshed === 'ok') {
				response = await send(path, options, generation);
			}
		}

		if (!options.allowSessionChange) assertAuthorizationGeneration(generation);
		const result = await unwrap<T>(response, options.allowSessionChange ? undefined : generation);
		if (!options.allowSessionChange) assertAuthorizationGeneration(generation);
		return result;
	} catch (error) {
		if (!options.allowSessionChange) assertAuthorizationGeneration(generation);
		throw error;
	}
}

async function send(path: string, options: RequestOptions, generation: number): Promise<Response> {
	assertAuthorizationGeneration(generation);
	const headers: Record<string, string> = { ...options.headers };

	// Заголовок ставим только когда тело действительно есть: на запросах
	// без тела он заставляет сервер разбирать пустоту.
	if (options.body !== undefined) headers['content-type'] = 'application/json';
	if (accessToken) headers.authorization = `Bearer ${accessToken}`;

	const method = options.method ?? (options.body === undefined ? 'GET' : 'POST');

	return fetchWithRetry(
		path,
		{
			method,
			headers,
			// Без этого браузер не пошлёт печенье с токеном обновления.
			credentials: 'same-origin',
			body: options.body === undefined ? undefined : JSON.stringify(options.body)
		},
		method === 'GET',
		generation
	);
}

function isMaintenance(value: unknown): value is MaintenanceState {
	if (!value || typeof value !== 'object') return false;
	const state = value as Record<string, unknown>;
	return typeof state.active === 'boolean' && typeof state.title === 'string';
}

async function unwrap<T>(response: Response, generation?: number): Promise<T> {
	let payload: unknown = null;

	try {
		payload = await response.json();
	} catch {
		payload = null;
	}
	if (generation !== undefined) assertAuthorizationGeneration(generation);

	if (!response.ok) {
		const data = (payload ?? {}) as Record<string, unknown>;

		/*
		 * Технические работы сервер объясняет прямо в отказе. Показывать при
		 * этом «не удалось выполнить запрос» незачем: экран работ уже всё
		 * сказал, а лишнее сообщение выглядело бы поломкой.
		 */
		if (response.status === 503 && isMaintenance(data.maintenance)) {
			maintenanceListener?.(data.maintenance);
		}
		/*
		 * Своё 500 сервер не отправляет: там, где он умеет объяснить, стоит 503
		 * с русским текстом. Значит 500 — это неожиданный сбой, и в теле лежит
		 * английское «Internal Server Error» от самого фреймворка. Показывать
		 * его человеку незачем: он ничего не объясняет и выглядит поломкой
		 * приложения, а не сервера.
		 */
		const message =
			response.status === 500
				? 'Ошибка на сервере. Попробуйте ещё раз'
				: typeof data.error === 'string'
					? data.error
					: 'Не удалось выполнить запрос';

		throw new ApiError(message, response.status, data);
	}

	return payload as T;
}

/**
 * Обновить токен доступа. Несколько одновременных запросов, получивших отказ,
 * дожидаются одного общего обновления, а не запускают по своему: иначе первый
 * же прокрутил бы токен обновления, и остальные получили бы отказ как при краже.
 */
async function refreshOnce(generation = authorizationGeneration): Promise<RefreshResult> {
	if (generation !== authorizationGeneration) return 'expired';
	if (refreshing?.generation === generation) return refreshing.promise;
	const run = { generation, promise: null as unknown as Promise<RefreshResult> };
	run.promise = withRefreshLock(async (): Promise<RefreshResult> => {
		try {
			if (generation !== authorizationGeneration) return 'expired';
			const response = await fetchTracked(
				'/api/auth/refresh',
				{ method: 'POST', credentials: 'same-origin' },
				true
			);
			if (generation !== authorizationGeneration) return 'expired';

			/*
			 * Сессия кончилась, только если это сказал сам сервер: 401, на отозванную —
			 * тоже. Всё остальное — 502 от прокси, пока API перезапускается при выкладке,
			 * 500, 429 — временный сбой: печенье с токеном обновления цело. Раньше любой
			 * неудачный ответ считался истечением, и человека выбрасывало на экран входа
			 * со стёртыми черновиками и сохранённой перепиской.
			 */
			if (response.status === 401 || response.status === 403) {
				accessToken = null;
				return 'expired';
			}
			if (!response.ok) return 'offline';

			const data = (await response.json()) as { accessToken: string };
			if (generation !== authorizationGeneration) return 'expired';
			accessToken = data.accessToken;

			return 'ok';
		} catch {
			/*
			 * Сетевой сбой — не повод считать сессию чужой: печенье с токеном
			 * обновления никуда не делось, и после связи всё продолжится.
			 * Раньше токен здесь стирался, и человек при обрыве оказывался на
			 * экране входа с QR-кодом, будто он вышел.
			 */
			return 'offline';
		}
	}).finally(() => {
		if (refreshing === run) refreshing = null;
	});
	refreshing = run;
	return run.promise;
}

/** Сколько ждать очереди на обновление у других вкладок. */
const REFRESH_LOCK_WAIT_MS = 25_000;

/*
 * Обновление токена — по одному на весь браузер, а не только на вкладку.
 *
 * Печенье с токеном обновления у вкладок общее, и каждое обновление его меняет.
 * Две вкладки, проснувшиеся разом (открыли ноутбук, телефон вернулся из фона),
 * предъявляли один и тот же токен: вторая приходила к серверу с уже погашенным,
 * а для него это признак кражи — он закрывал всю сессию, и человека выбрасывало
 * из аккаунта сразу везде.
 *
 * С замком вкладки идут по очереди: вторая отправляет запрос, когда первая уже
 * получила ответ и печенье сменилось. Где замков нет, страхует сервер — прежний
 * токен он ещё минуту принимает.
 */
async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
	const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
	if (!locks?.request) return task();

	let entered = false;
	try {
		return (await locks.request(
			'andresot-token-refresh',
			{ signal: AbortSignal.timeout(REFRESH_LOCK_WAIT_MS) },
			() => {
				entered = true;
				return task();
			}
		)) as T;
	} catch (error) {
		// Очереди не дождались — чужая вкладка застряла с замком. Обновиться без
		// очереди лучше, чем не обновиться вовсе.
		if (entered) throw error;
		return task();
	}
}

/** Попробовать восстановить сессию при запуске приложения. */
export async function restoreSession(): Promise<RefreshResult> {
	return refreshOnce();
}
